import React, { useState, useEffect } from 'react';
import { createClient } from '../lib/supabase/client';

const ContactForm = ({ type = 'basic', embedded = false, idPrefix = '' }) => {
  const id = (name) => (idPrefix ? `${idPrefix}${name}` : name);
  // Initialiser Google Ads
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'AW-16912204930');
    }
  }, []);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service: '',
    // Champs spécifiques pour demande de devis
    address: '',
    surface: '',
    credit_impot: 'non', // 'oui' ou 'non'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const supabase = createClient();
      
      const contactData = {
        type: type, // 'basic' ou 'ads'
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message,
        service: formData.service || null,
        // Champs spécifiques pour demande de devis
        address: formData.address || null,
        surface: formData.surface || null,
        credit_impot: formData.credit_impot || 'non',
        status: 'new'
        // Ne pas inclure created_at, il sera généré automatiquement par la base de données
      };

      const { error: insertError } = await supabase
        .from('contact_requests')
        .insert([contactData]);

      if (insertError) {
        console.error('Détails de l\'erreur:', insertError);
        throw insertError;
      }

      // Notification (optionnelle) : email via Supabase Edge Function
      // Ne doit jamais bloquer l'envoi du formulaire côté client.
      try {
        await supabase.functions.invoke('contact-email-notify', {
          body: {
            contact: contactData,
          },
        });
      } catch (notifyErr) {
        console.warn('Notification email non envoyée:', notifyErr);
      }

      // Déclencher l'événement de conversion Google Ads
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-16912204930/37wTCIqA1robEIKJr4A_'
        });
      }

      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        service: '',
        address: '',
        surface: '',
        credit_impot: 'non',
      });

      // Réinitialiser le message de succès après 5 secondes
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Erreur lors de l\'envoi du formulaire:', err);
      let errorMessage = 'Une erreur est survenue. Veuillez réessayer.';
      
      const rawMessage = err?.message ? String(err.message) : '';
      const rawDetails = err?.details ? String(err.details) : '';
      const rawHint = err?.hint ? String(err.hint) : '';
      const combined = `${rawMessage}\n${rawDetails}\n${rawHint}`.toLowerCase();

      if (rawMessage) {
        if (combined.includes('row level security') || combined.includes('row-level security') || combined.includes('rls')) {
          errorMessage = 'Une erreur de configuration empêche l\'envoi du formulaire. Veuillez nous contacter directement par téléphone ou email.';
        } else if (combined.includes('403') || combined.includes('permission')) {
          errorMessage = 'Erreur de permissions. Veuillez vérifier la configuration de la base de données.';
        } else if (combined.includes('violates')) {
          // Contrainte SQL (CHECK, NOT NULL, etc.)
          errorMessage = `Les données saisies ne sont pas valides. ${rawMessage}`;
        } else {
          errorMessage = `Erreur: ${rawMessage}`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formShellStyle = embedded
    ? {
        maxWidth: '100%',
        margin: 0,
        padding: '1rem 1.125rem',
        background: 'var(--color-white)',
        borderRadius: '10px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
      }
    : {
        maxWidth: '600px',
        margin: '0 auto',
        padding: '2rem',
        background: 'var(--color-white)',
        borderRadius: '12px',
        boxShadow: 'var(--shadow-md)',
      };

  const gap = embedded ? '0.75rem' : '1.5rem';
  const lblMb = embedded ? '0.3rem' : '0.5rem';
  const fieldStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: embedded ? '0.45rem 0.6rem' : '0.75rem',
    border: '1px solid rgba(0,0,0,0.12)',
    borderRadius: embedded ? '6px' : '8px',
    fontSize: embedded ? '0.875rem' : '1rem',
    fontFamily: 'inherit',
    color: 'var(--color-dark)',
    backgroundColor: 'var(--color-white)',
    caretColor: 'var(--color-dark)',
  };
  const labelStyle = {
    display: 'block',
    marginBottom: lblMb,
    fontWeight: '500',
    color: 'var(--color-dark)',
    fontSize: embedded ? '0.8125rem' : undefined,
  };
  const msgRows = embedded ? 3 : type === 'ads' ? 4 : 5;

  return (
    <form
      onSubmit={handleSubmit}
      className={`contact-form${embedded ? ' contact-form--embedded' : ''}`}
      style={formShellStyle}
    >
      {success && (
        <div style={{
          padding: embedded ? '0.65rem' : '1rem',
          marginBottom: gap,
          background: 'rgba(22, 163, 74, 0.1)',
          border: '1px solid rgba(22, 163, 74, 0.3)',
          borderRadius: '8px',
          color: 'var(--color-primary)',
          textAlign: 'center'
        }}>
          ✓ Votre message a été envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.
        </div>
      )}

      {error && (
        <div style={{
          padding: embedded ? '0.65rem' : '1rem',
          marginBottom: gap,
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#ef4444',
          textAlign: 'center'
        }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: gap }}>
        <label htmlFor={id('name')} style={labelStyle}>
          Nom complet <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <input
          type="text"
          id={id('name')}
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          style={fieldStyle}
          className="contact-form-field"
        />
      </div>

      <div style={{ marginBottom: gap }}>
        <label htmlFor={id('email')} style={labelStyle}>
          Email <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <input
          type="email"
          id={id('email')}
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          style={fieldStyle}
          className="contact-form-field"
        />
      </div>

      <div style={{ marginBottom: gap }}>
        <label htmlFor={id('phone')} style={labelStyle}>
          Téléphone
        </label>
        <input
          type="tel"
          id={id('phone')}
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          style={fieldStyle}
          className="contact-form-field"
        />
      </div>

      {type === 'basic' && (
        <div style={{ marginBottom: gap }}>
          <label htmlFor={id('service')} style={labelStyle}>
            Service souhaité
          </label>
          <select
            id={id('service')}
            name="service"
            value={formData.service}
            onChange={handleChange}
            className="contact-form-select"
          >
            <option value="">Sélectionnez un service</option>
            <option value="service-particulier">Service Particulier</option>
            <option value="service-entreprises">Service Entreprises</option>
            <option value="nettoyage-terrasse">Nettoyage de terrasse</option>
            <option value="elagage">Élagage d'arbres</option>
            <option value="tonte">La Tonte</option>
            <option value="taille-haie">Taille de haie</option>
            <option value="credit-impot">Crédit d'impôt jardin</option>
          </select>
        </div>
      )}

      {type === 'ads' && (
        <div style={{ marginBottom: gap }}>
          <label htmlFor={id('service-ads')} style={labelStyle}>
            Type de service souhaité
          </label>
          <select
            id={id('service-ads')}
            name="service"
            value={formData.service}
            onChange={handleChange}
            className="contact-form-select"
          >
            <option value="">Sélectionnez un service</option>
            <option value="entretien-complet">Entretien complet</option>
            <option value="tonte">Tonte de pelouse</option>
            <option value="taille-haie">Taille de haie</option>
            <option value="elagage">Élagage d'arbres</option>
            <option value="nettoyage-terrasse">Nettoyage de terrasse</option>
            <option value="service-particulier">Service Particulier</option>
            <option value="service-entreprises">Service Entreprises</option>
            <option value="autre">Autre</option>
          </select>
        </div>
      )}

      {type === 'ads' && (
        <>
          <div style={{ marginBottom: gap }}>
            <label htmlFor={id('address')} style={labelStyle}>
              Adresse du chantier <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              id={id('address')}
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              placeholder="Ex: 123 Rue de la République, 75001 Paris"
              style={fieldStyle}
              className="contact-form-field"
            />
          </div>

          <div style={{ marginBottom: gap }}>
            <label htmlFor={id('surface')} style={labelStyle}>
              Surface à entretenir (en m²)
            </label>
            <input
              type="number"
              id={id('surface')}
              name="surface"
              value={formData.surface}
              onChange={handleChange}
              placeholder="Ex: 500"
              min="0"
              style={fieldStyle}
              className="contact-form-field"
            />
          </div>

          <div style={{ 
            marginBottom: '1.5rem',
            padding: '1.5rem',
            background: 'linear-gradient(135deg, rgba(22, 163, 74, 0.1) 0%, rgba(22, 163, 74, 0.05) 100%)',
            borderRadius: '12px',
            border: '2px solid rgba(22, 163, 74, 0.2)'
          }}>
            <label style={{
              display: 'block',
              marginBottom: '1rem',
              fontWeight: '600',
              color: 'var(--color-primary)',
              fontSize: '1.1rem'
            }}>
              💰 Je veux bénéficier du crédit d'impôt de 50% <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <p style={{
              fontSize: '0.9rem',
              color: 'var(--color-gray)',
              marginBottom: '1rem',
              lineHeight: '1.6'
            }}>
              Réduisez vos dépenses de 50% grâce au crédit d'impôt jardin.
            </p>
            <div style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem 2rem',
                border: formData.credit_impot === 'oui' ? '2px solid var(--color-primary)' : '2px solid rgba(0,0,0,0.1)',
                borderRadius: '8px',
                background: formData.credit_impot === 'oui' ? 'var(--color-primary)' : 'var(--color-white)',
                color: formData.credit_impot === 'oui' ? 'var(--color-white)' : 'var(--color-dark)',
                transition: 'all 0.3s ease',
                fontWeight: '500',
                flex: 1,
                minWidth: '120px',
                justifyContent: 'center'
              }}>
                <input
                  type="radio"
                  name="credit_impot"
                  value="oui"
                  checked={formData.credit_impot === 'oui'}
                  onChange={handleChange}
                  required
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: 'var(--color-primary)'
                  }}
                />
                <span>Oui, je veux en bénéficier</span>
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                cursor: 'pointer',
                padding: '1rem 2rem',
                border: formData.credit_impot === 'non' ? '2px solid var(--color-primary)' : '2px solid rgba(0,0,0,0.1)',
                borderRadius: '8px',
                background: formData.credit_impot === 'non' ? 'var(--color-primary)' : 'var(--color-white)',
                color: formData.credit_impot === 'non' ? 'var(--color-white)' : 'var(--color-dark)',
                transition: 'all 0.3s ease',
                fontWeight: '500',
                flex: 1,
                minWidth: '120px',
                justifyContent: 'center'
              }}>
                <input
                  type="radio"
                  name="credit_impot"
                  value="non"
                  checked={formData.credit_impot === 'non'}
                  onChange={handleChange}
                  required
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    accentColor: 'var(--color-primary)'
                  }}
                />
                <span>Non</span>
              </label>
            </div>
          </div>
        </>
      )}

      <div style={{ marginBottom: gap }}>
        <label htmlFor={id('message')} style={labelStyle}>
          {type === 'ads' ? 'Détails de votre projet' : 'Message'} <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <textarea
          id={id('message')}
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={String(msgRows)}
          placeholder={type === 'ads' ? 'Décrivez vos besoins, la fréquence souhaitée, vos préférences...' : 'Votre message...'}
          style={{
            ...fieldStyle,
            resize: 'vertical',
            minHeight: embedded ? '4.5rem' : undefined,
          }}
          className="contact-form-field"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="modern-button"
        style={{
          width: '100%',
          padding: embedded ? '0.65rem 1rem' : '1.25rem',
          fontSize: embedded ? '0.9rem' : '1.1rem',
          fontWeight: '600',
          cursor: loading ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.7 : 1,
          background: type === 'ads' ? 'var(--color-primary)' : undefined,
          color: type === 'ads' ? 'var(--color-white)' : undefined
        }}
      >
        {loading ? 'Envoi en cours...' : type === 'ads' ? 'Demander mon devis gratuit' : 'Envoyer'}
      </button>
      
      {type === 'ads' && (
        <p style={{
          marginTop: '1rem',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: 'var(--color-gray)'
        }}>
          ✓ Devis gratuit et sans engagement • Réponse sous 24h
        </p>
      )}
    </form>
  );
};

export default ContactForm;

